import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
    Bug,
    Play,
    StepForward,
    Square,
    CircleDot,
    AlertCircle,
    X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TestCase } from '@/hooks/useWorkflowDebug';

interface WorkflowDebugToolbarProps {
    isDebugMode: boolean;
    isDebugging: boolean;
    isPaused: boolean;
    currentNode: string | null;
    runId: string | null;
    error: string | null;
    testCases: TestCase[];
    selectedTestCase: string | null;
    breakpointCount: number;
    onToggleDebugMode: () => void;
    onSelectTestCase: (name: string) => void;
    onStart: () => void;
    onStep: () => void;
    onContinue: () => void;
    onStop: () => void;
}

export function WorkflowDebugToolbar({
    isDebugMode,
    isDebugging,
    isPaused,
    currentNode,
    error,
    testCases,
    selectedTestCase,
    breakpointCount,
    onToggleDebugMode,
    onSelectTestCase,
    onStart,
    onStep,
    onContinue,
    onStop,
}: WorkflowDebugToolbarProps) {
    if (!isDebugMode) {
        return (
            <Button
                variant="outline"
                size="sm"
                onClick={onToggleDebugMode}
                className="gap-1.5"
            >
                <Bug size={16} className="text-orange-500" />
                <span className="hidden sm:inline">Debug</span>
            </Button>
        );
    }

    return (
        <div className="flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-1.5 shadow-sm min-w-[520px]">
            {/* Debug Mode Indicator */}
            <div className="flex items-center gap-1.5 border-r border-orange-200 pr-3">
                <Bug size={16} className="text-orange-600" />
                <span className="text-sm font-medium text-orange-700">Debug</span>
            </div>

            {/* Test Case Selector (when not debugging) */}
            {!isDebugging && (
                <Select
                    value={selectedTestCase || ''}
                    onValueChange={onSelectTestCase}
                >
                    <SelectTrigger className="h-8 w-[280px] bg-white text-sm">
                        <SelectValue placeholder="Select test case..." />
                    </SelectTrigger>
                    <SelectContent>
                        {testCases.map((tc) => (
                            <SelectItem key={tc.name} value={tc.name}>
                                {tc.name}
                            </SelectItem>
                        ))}
                        {testCases.length === 0 && (
                            <div className="px-2 py-1.5 text-sm text-slate-500">
                                No test cases found
                            </div>
                        )}
                    </SelectContent>
                </Select>
            )}

            {/* Active Test Case Badge (when debugging) */}
            {isDebugging && selectedTestCase && (
                <Badge variant="secondary" className="bg-white">
                    {selectedTestCase}
                </Badge>
            )}

            {/* Control Buttons */}
            <div className="flex items-center gap-1 border-l border-orange-200 pl-2">
                {!isDebugging ? (
                    <>
                        {/* Start Button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onStart}
                            disabled={!selectedTestCase}
                            className="h-8 gap-1 text-green-600 hover:bg-green-100 hover:text-green-700"
                        >
                            <Play size={16} />
                            <span className="hidden sm:inline">Start</span>
                        </Button>

                        {/* Breakpoint Count */}
                        {breakpointCount > 0 && (
                            <Badge variant="outline" className="ml-1 gap-1">
                                <CircleDot size={12} className="text-red-500" />
                                {breakpointCount}
                            </Badge>
                        )}
                    </>
                ) : (
                    <>
                        {/* Continue Button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onContinue}
                            disabled={!isPaused}
                            className={cn(
                                'h-8 gap-1',
                                isPaused
                                    ? 'text-green-600 hover:bg-green-100'
                                    : 'text-slate-400'
                            )}
                            title="Continue (F5)"
                        >
                            <Play size={16} />
                            <span className="hidden sm:inline">Continue</span>
                        </Button>

                        {/* Step Button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onStep}
                            disabled={!isPaused}
                            className={cn(
                                'h-8 gap-1',
                                isPaused
                                    ? 'text-blue-600 hover:bg-blue-100'
                                    : 'text-slate-400'
                            )}
                            title="Step Over (F10)"
                        >
                            <StepForward size={16} />
                            <span className="hidden sm:inline">Step</span>
                        </Button>

                        {/* Stop Button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onStop}
                            className="h-8 gap-1 text-red-600 hover:bg-red-100 hover:text-red-700"
                            title="Stop (Shift+F5)"
                        >
                            <Square size={16} />
                            <span className="hidden sm:inline">Stop</span>
                        </Button>
                    </>
                )}

                {/* Exit Debug Mode */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggleDebugMode}
                    className="ml-1 h-7 px-2 text-xs text-orange-600 hover:bg-orange-100"
                >
                    <X className="mr-1 h-3 w-3" />
                    Exit Debug
                </Button>
            </div>

            {/* Status Indicator */}
            {isDebugging && (
                <div className="flex items-center gap-2 border-l border-orange-200 pl-3">
                    <div
                        className={cn(
                            'h-2 w-2 rounded-full',
                            isPaused ? 'animate-pulse bg-yellow-500' : 'bg-green-500'
                        )}
                    />
                    <span className="text-sm text-slate-600">
                        {isPaused ? 'Paused' : 'Running'}
                        {currentNode && (
                            <span className="ml-1 font-mono text-slate-800">@ {currentNode}</span>
                        )}
                    </span>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle size={14} />
                    {error}
                </div>
            )}
        </div>
    );
}

